import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

// Context pour propager le variant aux sous-composants sans prop drilling
const DialogVariantContext = React.createContext<"industrial" | undefined>(undefined);
const useDialogVariant = () => React.useContext(DialogVariantContext);

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[100] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    variant?: "industrial";
  }
>(({ className, children, variant, onPointerDownOutside, onInteractOutside, onFocusOutside, ...props }, ref) => {
  const isIndustrial = variant === "industrial";

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-variant={variant}
        className={cn(
          "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          isIndustrial && "bg-[#111111] border-[#2A2A2A] rounded-[6px] text-[#F2F2F2] shadow-[0_32px_80px_rgba(0,0,0,0.9),_inset_0_1px_0_rgba(255,255,255,0.04)]",
          className,
        )}
        // Empêcher la fermeture automatique quand on clique en dehors ou qu'on perd le focus
        // Le modal ne se ferme que via les boutons Annuler/Valider/X
        onPointerDownOutside={(e) => {
          e.preventDefault();
          onPointerDownOutside?.(e);
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
          onInteractOutside?.(e);
        }}
        // Empêcher la fermeture quand le focus quitte le dialog (ex: tab switch, Alt+Tab, minimiser)
        onFocusOutside={(e) => {
          e.preventDefault();
          onFocusOutside?.(e);
        }}
        {...props}
      >
        <DialogVariantContext.Provider value={variant}>
          {children}
        </DialogVariantContext.Provider>
        <DialogPrimitive.Close
          className={cn(
            "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
            isIndustrial && "bg-[#1C1C1C] border border-[#333] rounded-[6px] w-[30px] h-[30px] flex items-center justify-center opacity-100 text-white/35 hover:border-[#444] hover:text-white/60 hover:opacity-100 focus:ring-0 focus:ring-offset-0",
          )}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const variant = useDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        isIndustrial && "border-b border-[#2A2A2A] -mx-6 -mt-6 px-5 py-4 rounded-t-[6px] space-y-1",
        className,
      )}
      style={isIndustrial ? { background: "linear-gradient(90deg, #1C1C1C, #181818)" } : undefined}
      {...props}
    />
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const variant = useDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        isIndustrial && "border-t border-[#1E1E1E] -mx-6 -mb-6 px-5 py-4",
        className,
      )}
      {...props}
    />
  );
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, children, ...props }, ref) => {
  const variant = useDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        isIndustrial && "text-[#F2F2F2] font-bold flex items-center gap-3",
        className,
      )}
      {...props}
    >
      {isIndustrial ? (
        <>
          <div
            className="w-[3px] h-[22px] rounded-[2px] flex-shrink-0"
            style={{ background: "linear-gradient(180deg, #F7B500, #FF9500)" }}
          />
          {children}
        </>
      ) : (
        children
      )}
    </DialogPrimitive.Title>
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const variant = useDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(
        "text-sm text-muted-foreground",
        isIndustrial && "text-white/25",
        className,
      )}
      {...props}
    />
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
